"use client";

import Link from "next/link";
import { useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import useUtility from "@/app/_hooks/useUtility";
import FooterSkeleton from "./_skeletons/FooterSkeleton";
import { useSection } from "../_sections/_hooks/useSection";
import useSubscribe from "./_hooks/useSubscribe";
import Image from "next/image";
import FooterMenu from "./_components/FooterMenu";

export const Footer = () => {
  const { trans } = useUtility();
  const { content: subscribeContent, loading: subscribeLoading } =
    useSection("subscribe");
  const {
    content: socialContent,
    elements: socialIcons,
    loading: socialLoading,
  } = useSection("social_icon");

  const { content: footerContent, loading: footerLoading } =
    useSection("footer");

  const { isSubmitting, SubscribeSchema, handleSubscribe } = useSubscribe();

  const shortcode = useSelector(
    (state) => state?.extension?.whatsapp?.data?.data?.extension?.shortcode,
  );

  const number =
    shortcode?.phoneNumber?.value?.replace(/\+/g, "") || "0000000000";

  if (subscribeLoading || socialLoading || footerLoading) {
    return <FooterSkeleton />;
  }

  // Subscribe Section
  const logoUrl = subscribeContent?.logo
    ? `${process.env.NEXT_PUBLIC_API_URL}/assets/images/frontend/subscribe/${subscribeContent.logo}`
    : "#"; // Default logo

  return (
    <footer className="footer-area">
      {/* Subscribe Section */}
      <div className="footer-area__top">
        <div className="container">
          <div className="row align-items-center justify-content-center">
            <div className="col-xl-4 col-lg-2 col-md-4 col-sm-6 col-xsm-5 col-xxsm-5">
              <div className="footer-logo">
                <Link href="/">
                  <Image src={logoUrl} alt="Logo" width={82} height={63} />
                </Link>
              </div>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-5 col-sm-6 col-xsm-7 col-xxsm-7">
              <div className="cta-content">
                <h6 className="cta-content__title">
                  {trans(subscribeContent?.heading)}
                </h6>
                <p className="cta-content__desc">
                  {trans(subscribeContent?.subheading)}
                </p>
              </div>
            </div>
            <div className="col-xl-5 col-lg-6 col-md-9 col-sm-12">
              <Formik
                initialValues={{ email: "" }}
                validationSchema={SubscribeSchema}
                onSubmit={handleSubscribe}
              >
                {({ errors, touched }) => (
                  <Form className="cta-form">
                    <div className="form-group position-relative mb-0">
                      <Field
                        className="form--control"
                        type="email"
                        name="email"
                        placeholder={trans("Your email address")}
                      />
                      {errors.email && touched.email && (
                        <div className="error-message">
                          {trans(errors.email)}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="btn btn--warning"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? trans("Submitting...")
                          : trans(subscribeContent?.button_name)}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="container">
        <FooterMenu number={number} />
        {/* Social Icons */}
        <div className="row">
          <div className="col-12">
            <ul className="social-list">
              {socialIcons?.map((icon, index) => (
                <li key={index} className="social-list__item">
                  <Link
                    href={icon?.url || "#"}
                    target="_blank"
                    className="social-list__link"
                    dangerouslySetInnerHTML={{
                      __html: icon.icon_code,
                    }} // Render SVG safely
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
        {/* Footer Bottom */}
        <div className="row">
          <div className="col-12">
            <div className="bottom-footer text-center">
              <p className="bottom-footer__text">
                {trans(footerContent?.top_text)}
              </p>
              <p className="bottom-footer__text">
                {trans(footerContent?.bottom_text)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
